import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthProvider';
import { useAlert } from './AlertContext';
import ImpersonationBannerView from './ImpersonationBannerView';
import { useImpersonationStatus, useStopImpersonation } from '../hooks/impersonation';

/**
 * Container: dauerhaft sichtbare Warnleiste während einer Impersonation. Quelle
 * ist der serverseitige Status (überlebt Reload/Navigation). Auf allen Seiten
 * sichtbar, da in Layout oberhalb des Inhalts gerendert.
 */
const ImpersonationBanner: React.FC = () => {
    const { status } = useImpersonationStatus();
    const { stopImpersonation, isStopping } = useStopImpersonation();
    const { applyAuthSession, logout } = useAuth();
    const { showAlert } = useAlert();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    if (!status?.active) {
        return null;
    }

    const name = status.impersonated_user?.display ?? 'unbekannt';

    const handleStop = async () => {
        try {
            const payload = await stopImpersonation();
            await queryClient.cancelQueries();
            applyAuthSession(payload);
            queryClient.clear();
            navigate(`/accounts?userId=${payload.last_impersonated_user_id}`);
        } catch {
            // Session nicht mehr wiederherstellbar → sicherer Ausstieg via Logout.
            showAlert({ severity: 'error', text: 'Impersonation konnte nicht beendet werden. Bitte neu anmelden.' });
            await logout();
            navigate('/');
        }
    };

    return <ImpersonationBannerView name={name} onStop={handleStop} stopping={isStopping} />;
};

export default ImpersonationBanner;
